<template>
    <admin-header />
    <admin-menu />
    
    <div class="p-4 ml-20 text-textOnBkg dark:text-dark.textOnBkg">
        <div class="flex items-center gap-5 mb-3">
            <h1 class="text-xl">{{ $t('permissionsManagement') }}</h1>
            <MazBtn size="sm" @click="openAddPermissionModal">{{ $t('addNewPermission') }}</MazBtn>
            <MazBtn size="sm" @click="openOrderPermissionsModal">Order Permissions</MazBtn>
        </div>
        
        <!-- Permissions Table -->
        <table class="min-w-full border-collapse bg-tableBG dark:bg-dark.tableBG rounded-lg">
            <thead>
                <tr class="bg-gray-200 dark:bg-dark.panels dark:text-dark.textOnBkg">
                    <th class="border border-sepBorder dark:border-dark.sepBorder p-2 text-left">{{ $t('category') }}</th>
                    <th class="border border-sepBorder dark:border-dark.sepBorder p-2 text-left">{{ $t('permissionName') }}</th>
                    <th class="border border-sepBorder dark:border-dark.sepBorder p-2 text-left">{{ $t('scope') }}</th>
                    <th class="border border-sepBorder dark:border-dark.sepBorder p-2 text-left">{{ $t('description') }}</th>
                    <th class="border border-sepBorder dark:border-dark.sepBorder p-2 text-left">{{ $t('actions') }}</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="(category, catIndex) in permissions" :key="catIndex">
                    <tr v-for="(permission, permIndex) in category.permissions" :key="permission.id" class="even:bg-gray-50 dark:even:bg-dark.tableRow1">
                        <td v-if="permIndex === 0" :rowspan="category.permissions.length" class="border border-sepBorder dark:border-dark.sepBorder p-2">
                            {{ category.category_name }}
                        </td>
                        <td class="border border-sepBorder dark:border-dark.sepBorder p-1 px-2">{{ permission.name }}</td>
                        <td class="border border-sepBorder dark:border-dark.sepBorder p-1 px-2">{{ permission.scope }}</td>
                        <td class="border border-sepBorder dark:border-dark.sepBorder p-1 px-2">{{ permission.description }}</td>
                        <td class="border border-sepBorder dark:border-dark.sepBorder p-1 px-2">
                            <MazBtn size="xs" @click="openEditPermissionModal(permission, category.category_name)">{{ $t('edit') }}</MazBtn>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        
        <!-- Add Permission Modal -->
        <MazDialog v-model="showAddPermissionModal">
            <template #header>
                <h2 class="p-5 pb-0">{{ $t('addNewPermission') }}</h2>
            </template>
            <MazSelect v-model="newPermissionCategory" :options="categoryNames" :label="$t('selectCategory')" />
            <MazInput v-model="newPermissionName" :label="$t('permissionName')" class="mt-2" />
            <MazInput v-model="newPermissionScope" type="number" :label="$t('scope')" class="mt-2" />
            <MazInput v-model="newPermissionDescription" :label="$t('description')" class="mt-2 w-full" />
            <div class="mt-4 space-x-2">
                <MazBtn @click="addPermission">{{ $t('save') }}</MazBtn>
                <MazBtn @click="showAddPermissionModal = false">{{ $t('cancel') }}</MazBtn>
            </div>
        </MazDialog>
        
        <!-- Edit Permission Modal -->
        <MazDialog v-model="showEditPermissionModal">
            <template #header>
                <h2 class="p-5 pb-0">{{ $t('editPermission') }}</h2>
            </template>
            <MazSelect v-model="editCategory" :options="categoryNames" :label="$t('selectCategory')" />
            <MazInput v-model="editName" :label="$t('permissionName')" class="mt-2" />
            <MazInput v-model="editScope" type="number" :label="$t('scope')" class="mt-2" />
            <MazInput v-model="editDescription" :label="$t('description')" class="mt-2 w-full" />
            <div class="mt-4 space-x-2">
                <MazBtn @click="saveEditPermission">{{ $t('save') }}</MazBtn>
                <MazBtn @click="showEditPermissionModal = false">{{ $t('cancel') }}</MazBtn>
            </div>
        </MazDialog>

        <!-- Order Permissions Modal -->
        <MazDialog v-model="showOrderPermissionsModal">
            <template #header>
                <h2 class="p-5 pb-2 font-semibold text-lg">Order Permissions</h2>
            </template>
            <section class="grid grid-cols-3">
                <div v-for="(category, catIndex) in permissions" :key="catIndex" class="p-4 border border-sepBorder dark:border-dark.sepBorder rounded">
                    <h3 class="text-lg font-bold mb-2">{{ category.category_name }}</h3>
                    <draggable v-model="category.permissions" itemKey="id" @change="debounceSaveOrder">
                        <template #item="{ element }">
                            <div class="p-1 px-2 border rounded mb-1.5">
                                {{ element.name }}
                            </div>
                        </template>
                    </draggable>
                </div>
            </section>
        </MazDialog>
    </div>
</template>

<script setup>
import adminHeader from '@/components/admin/AdminHeader.vue';
import adminMenu from '@/components/admin/AdminMenu.vue';
import _ from 'lodash';
import draggable from 'vuedraggable';
</script>

<script>
import { update_permissions_order, get_all_permissions, save_new_permission, update_permission } from '@/api/admin_api';

export default {
    name: 'PermissionsTable',
    components: {
        adminHeader, adminMenu, draggable
    },
    data() {
        return {
            permissions: [],
            showAddPermissionModal: false,
            showEditPermissionModal: false,
            showOrderPermissionsModal: false,
            currentEditingPermission: null,
            newPermissionCategory: '',
            newPermissionName: '',
            newPermissionScope: 0,
            newPermissionDescription: '',
            editName: '',
            editDescription: '',
            editScope: 0,
            editCategory: '',
            editId: 0,
        };
    },
    computed: {
        categoryNames() {
            return this.permissions.map((c) => c.category_name);
        },
    },
    methods: {
        // Open Add Permission Modal
        openAddPermissionModal() {
            this.newPermissionCategory = '';
            this.newPermissionName = '';
            this.newPermissionScope = 0;
            this.newPermissionDescription = '';
            this.showAddPermissionModal = true;
        },
        // Add New Permission
        async addPermission() {
            if (this.newPermissionCategory && this.newPermissionName.trim()) {
                const category = this.permissions.find((c) => c.category_name === this.newPermissionCategory);
                if (category) {
                    const newPermission = {
                        name: this.newPermissionName.trim(),
                        category: this.newPermissionCategory,
                        scope: this.newPermissionScope,
                        description: this.newPermissionDescription.trim(),
                    };
                    console.log(newPermission);
                    //respond with permission ID to save it
                    await save_new_permission(this.$cookies.get('admin_auth_token'), newPermission);
                    category.permissions.push(newPermission);
                    this.showAddPermissionModal = false;
                }
            }
        },
        // Open Edit Permission Modal
        openEditPermissionModal(permission, categoryName) {
            this.currentEditingPermission = permission;
            this.editName = permission.name;
            this.editDescription = permission.description;
            this.editScope = permission.scope;
            this.editCategory = categoryName;
            this.editId = permission.id;
            console.log(categoryName);
            this.showEditPermissionModal = true;
        },
        // Save Edited Permission
        async saveEditPermission() {
            if (this.currentEditingPermission && this.editName.trim()) {
                this.currentEditingPermission.permission_id = this.editId;
                this.currentEditingPermission.name = this.editName.trim();
                this.currentEditingPermission.description = this.editDescription.trim();
                this.currentEditingPermission.scope = this.editScope;
                this.currentEditingPermission.category = this.editCategory;

                await update_permission(this.$cookies.get('admin_auth_token'), this.currentEditingPermission);
                this.showEditPermissionModal = false;
            }
        },
        // Open Order Permissions Modal
        openOrderPermissionsModal() {
            this.showOrderPermissionsModal = true;
        },
        // Save Permissions Order
        async savePermissionsOrder() {
            const newPermissionsOrder = this.permissions.map((category) => ({
                category_name: category.category_name,
                permissions: category.permissions.map((permission, index) => ({
                    id: permission.id,
                    order: index + 1,
                })),
            }));
            await update_permissions_order(this.$cookies.get('admin_auth_token'), newPermissionsOrder);
        },
        // Debounced Save Order
        debounceSaveOrder: _.debounce(function () {
            this.savePermissionsOrder();
        }, 1000),
    },
    async created() {
        this.permissions = await get_all_permissions(this.$cookies.get('admin_auth_token'));
        
        this.nextId = Math.max(...this.permissions.map((c) => Math.max(...c.permissions.map((p) => p.id)))) + 1;
    }
};
</script>